'use client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { translations, type Lang, type T } from '@/lib/i18n'

type LanguageCtx = {
  lang: Lang
  setLang: (l: Lang) => void
  t: T
  isRTL: boolean
}

const LanguageContext = createContext<LanguageCtx>({
  lang: 'en',
  setLang: () => {},
  t: translations.en,
  isRTL: false,
})

function writeLangCookie(l: Lang) {
  document.cookie = `mrn-lang=${l}; path=/; max-age=31536000; SameSite=Lax`
}

// Half-duration of the language crossfade. A full switch is fade-out (this) →
// swap content + dir while covered → fade-in (this), so ~2× this overall.
const FADE_MS = 220

export function LanguageProvider({
  children,
  initialLang = 'en',
}: {
  children: React.ReactNode
  initialLang?: Lang
}) {
  // Seed from the server-detected cookie so the first client render matches
  // the SSR html lang/dir attributes. Avoids a flash on switch.
  const [lang, setLangState] = useState<Lang>(initialLang)
  // Drives the full-viewport surface veil that masks the content swap, so the
  // language change reads as a soft dissolve instead of an instant pop.
  const [covering, setCovering] = useState(false)
  const timers = useRef<number[]>([])

  const commit = useCallback((l: Lang) => {
    setLangState(l)
    localStorage.setItem('mrn-lang', l)
    writeLangCookie(l)
  }, [])

  const setLang = useCallback(
    (l: Lang) => {
      if (l === lang) return

      const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (reduce) {
        commit(l)
        return
      }

      // Fade the veil up; once it's fully covering, swap the language (text +
      // dir flip happen behind it), then fade the veil back down to reveal.
      setCovering(true)
      const t1 = window.setTimeout(() => {
        commit(l)
        // A short beat lets the new content + RTL reflow paint before reveal.
        const t2 = window.setTimeout(() => setCovering(false), 60)
        timers.current.push(t2)
      }, FADE_MS)
      timers.current.push(t1)
    },
    [lang, commit],
  )

  // Hydrate from localStorage if a previous session set it. Instant (no fade)
  // — this is initial load, there's nothing on screen to transition from.
  useEffect(() => {
    const saved = localStorage.getItem('mrn-lang') as Lang | null
    // TEMP (revert this week): EN + AR disabled — only honor a saved 'fa'.
    // Original: if (saved === 'en' || saved === 'fa' || saved === 'ar') {
    if (saved === 'fa') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLangState(saved)
      writeLangCookie(saved)
    }
  }, [])

  useEffect(() => {
    const isRTL = lang === 'fa' || lang === 'ar'
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
    document.documentElement.lang = lang
  }, [lang])

  // Clear any in-flight transition timers on unmount.
  useEffect(() => {
    const t = timers.current
    return () => t.forEach((id) => clearTimeout(id))
  }, [])

  return (
    <LanguageContext.Provider
      value={{
        lang,
        setLang,
        t: translations[lang] as T,
        isRTL: lang === 'fa' || lang === 'ar',
      }}
    >
      {children}
      {/* Crossfade veil. Sits above everything (incl. navbar z-30 + toggle
          z-40), painted in the page surface colour so the swap dissolves
          through the background. pointer-events-none keeps it from trapping
          clicks during the brief fade. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[60] bg-surface"
        style={{
          opacity: covering ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease`,
        }}
      />
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  return useContext(LanguageContext)
}
